import type { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import type { Observable } from 'rxjs';
import type { SystemDomain } from '@integrations/system-domains';
import type { Domain } from '@common/types/domain.type';

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostListener,
    Input,
    type OnInit,
    computed,
    effect,
    inject,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { take, tap, withLatestFrom } from 'rxjs/operators';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { type UpdateSiteRequest } from '@common/types/site.type';
import { ESaveNameErrorCodes, ESaveSubdomainErrorCodes } from '@common/enums/error-cores.enum';
import { AmplitudeAnalyticsService } from '@common/services/amplitude-analytics.service';

import { RouterFacade } from '@web/store/router/router.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { injectSystemDomainsQuery } from '@integrations/system-domains';

import SiteActions from '@web/store/website/site/site.actions';
import { AsyncPipe } from '@angular/common';
import { SubDomainValidatorDirective } from '@web/core/validators/sub-domain.validator';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TranslateModule } from '@ngx-translate/core';
import { UserFacade } from '@web/store/user/user.facade';

@Component({
    selector: 'web-create-new-website-modal',
    templateUrl: './create-new-website-modal.component.html',
    styleUrls: ['./create-new-website-modal.component.less'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [ReactiveFormsModule, SubDomainValidatorDirective, BsDropdownModule, AsyncPipe, TranslateModule],
})
export class CreateNewWebsiteModalComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly actions$ = inject(Actions);
    private readonly bsModalRef = inject(BsModalRef);
    private readonly fb = inject(UntypedFormBuilder);
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly siteFacade = inject(SiteFacade);
    private readonly pagesFacade = inject(PagesFacade);
    private readonly routerFacade = inject(RouterFacade);
    private readonly userFacade: UserFacade = inject(UserFacade);

    public readonly systemDomainsQuery = injectSystemDomainsQuery();

    @Input() public siteId!: string;
    @Input() public initialName!: string;
    @Input() public initialDomain!: Domain;
    @Input() public exit: boolean;
    @Input() public publish: boolean;
    @Input() public navigateTo: { pageId?: string; siteId?: string; isNew?: boolean };

    public saveErrorCode: string;
    public form: UntypedFormGroup;

    public readonly NAME_MIN_LENGTH: number = 1;
    public readonly isUserTariffFree$: Observable<boolean> = this.userFacade.isUserTariffFree$;

    public readonly disabledDomainsSet: Set<string> = new Set([
        'sendpulse.academy',
        'minisite.ai',
        'pulse.is',
        'sendpulse.online',
        'sendpulse.tech',
    ]);

    public readonly systemDomains = computed(() => this.systemDomainsQuery.data() ?? []);
    public readonly filteredSystemDomains = computed(() =>
        this.systemDomains().filter((domain) => !this.disabledDomainsSet.has(domain.name))
    );

    constructor() {
        this.initForm();

        let formDataInitialized = false;
        effect(() => {
            const domains = this.systemDomainsQuery.data();
            if (!domains?.length || formDataInitialized) return;
            formDataInitialized = true;

            untracked(() => {
                const domain = domains.find((item: SystemDomain) => item.id === this.initialDomain?.system_domain_id);
                const filteredDomains = domains.filter(
                    (domain: SystemDomain) => !this.disabledDomainsSet.has(domain.name)
                );

                this.selectSystemDomain(domain ?? filteredDomains[0]);

                let subDomain = this.initialDomain?.name?.split('.')[0];
                if (domain) {
                    subDomain = this.initialDomain?.name?.split(`.${domain.name}`)[0];
                }

                this.form.patchValue({ name: this.initialName, subDomain });
                this.changeDetector.detectChanges();
            });
        });
    }

    public ngOnInit(): void {
        this.actions$
            .pipe(ofType(SiteActions.saveSiteSuccess), takeUntilDestroyed(this.destroyRef))
            .subscribe(({ site }) => {
                this.siteFacade.updateSite(Number(site.id), this.payload, true);
            });

        this.actions$
            .pipe(ofType(SiteActions.saveSiteFailure), takeUntilDestroyed(this.destroyRef))
            .subscribe(({ error }) => {
                this.form.enable({ emitEvent: false });
                this.saveErrorCode = error;
                this.changeDetector.detectChanges();
            });

        this.actions$.pipe(ofType(SiteActions.updateSiteSuccess), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (!this.publish) {
                this.bsModalRef.hide();
            }

            if (!this.form.disabled) {
                return;
            }

            this.routerFacade.routerParams$.pipe(take(1)).subscribe((params) => {
                this.pagesFacade.activatePage(params.siteId, params.pageId);
                this.bsModalRef.hide();
            });
        });

        this.actions$.pipe(ofType(SiteActions.updateSiteFailure), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.form.enable({ emitEvent: false });
            this.changeDetector.detectChanges();
        });

        this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => (this.saveErrorCode = null));
    }

    private initForm(): void {
        this.form = this.fb.group({
            name: [null, [Validators.required, Validators.minLength(this.NAME_MIN_LENGTH)]],
            domain: null,
            subDomain: [null, [Validators.required]],
        });
    }

    public selectSystemDomain(domain: SystemDomain): void {
        this.domain.setValue(domain);
        this.subDomain.updateValueAndValidity({ emitEvent: false });
    }

    public onConfirm(): void {
        if (this.form.invalid) {
            return;
        }

        this.saveErrorCode = null;
        this.form.disable({ emitEvent: false });

        this.siteFacade.setNewSiteData(this.payload as any);
        this.routerFacade.routerParams$
            .pipe(
                take(1),
                withLatestFrom(this.siteFacade.isNewSite$.pipe(take(1))),
                tap(([routerParams, isNewSite]) => {
                    if (isNewSite) {
                        AmplitudeAnalyticsService.sendEvent({ event: 'sites_first_domain_saving' });
                    }

                    this.siteFacade.saveSiteRequest(false, false, routerParams.pageId, this.navigateTo);
                })
            )
            .subscribe();
    }

    public onCancel(): void {
        this.bsModalRef.hide();
    }

    @HostListener('document:keydown.enter')
    public enterKeyEvent(): void {
        if (this.form.valid && !this.systemDomainsQuery.isPending()) {
            this.onConfirm();
        }
    }

    private get payload(): UpdateSiteRequest {
        let domainValue = this.subDomain.value;

        if (domainValue.startsWith('http://')) {
            domainValue = domainValue.replace('http://', '');
        }

        if (domainValue.startsWith('https://')) {
            domainValue = domainValue.replace('https://', '');
        }

        const payload: UpdateSiteRequest = {
            domain: `${domainValue}.${this.domain.value.name}`,
        };

        if (this.name.dirty && this.name.value !== this.initialName) {
            payload.name = this.name.value;
        }

        return payload;
    }

    public get name(): UntypedFormControl {
        return this.form.get('name') as UntypedFormControl;
    }

    public get domain(): UntypedFormControl {
        return this.form.get('domain') as UntypedFormControl;
    }

    public get subDomain(): UntypedFormControl {
        return this.form.get('subDomain') as UntypedFormControl;
    }

    public get subDomainHasError(): boolean {
        return this.subDomainAlreadyExistsError || this.subDomainInvalidError;
    }

    public get subDomainAlreadyExistsError(): boolean {
        return this.subDomain.hasError(ESaveSubdomainErrorCodes.DOMAIN_ALREADY_EXISTS);
    }

    public get subDomainInvalidError(): boolean {
        return this.subDomain.hasError(ESaveSubdomainErrorCodes.INVALID_DOMAIN);
    }

    public get nameAlreadyUsedError(): boolean {
        return this.saveErrorCode === ESaveNameErrorCodes.SITE_NAME_ALREADY_USED;
    }
}
